import React, { useEffect } from 'react';
import { Button, PdficonColorIcon, Tooltip, useSnackbar } from 'ui-components';
import { logger } from '~/core/logger/logger';

import { useExportToPDF } from './useExportToPDF';

interface Props {
  listId: string;
  fullWidth?: boolean;
  handleSuccess?: () => void;
  isDisabled?: boolean;
  disabledTooltip?: string;
}

export const ExportToPDF: React.FC<Props> = ({
  listId,
  fullWidth,
  handleSuccess,
  isDisabled,
  disabledTooltip,
}: Props) => {
  const exportToPDF = useExportToPDF(listId);
  const snackbar = useSnackbar();

  const handleExport = () => exportToPDF.mutate();

  useEffect(() => {
    if (exportToPDF.isError) {
      snackbar.error({
        description: 'There was an error exporting the PDF',
        duration: 9000,
        isCloseable: true,
      });
      logger
        .withMetadata({
          listId,
          section: 'pdf-generation',
          error: exportToPDF.error,
        })
        .error('Error exporting the Saved List PDF');
    }
  }, [exportToPDF.isError, snackbar]);

  useEffect(() => {
    if (exportToPDF.isSuccess) {
      handleSuccess?.();
      logger
        .withMetadata({
          listId,
          section: 'pdf-generation',
        })
        .info('Saved List PDF exported successfully');
    }
  }, [exportToPDF.isSuccess, handleSuccess]);

  if (disabledTooltip && isDisabled) {
    return (
      <Tooltip label={disabledTooltip} placement="top">
        <div className={fullWidth ? 'w-full' : 'w-1/2'}>
          <Button
            aria-label="Export .pdf"
            variant="tertiary"
            leftIcon={<PdficonColorIcon />}
            isDisabled
            className="w-full"
          >
            .pdf
          </Button>
        </div>
      </Tooltip>
    );
  }

  return (
    <Button
      aria-label="Export .pdf"
      variant="tertiary"
      leftIcon={<PdficonColorIcon />}
      onClick={handleExport}
      isLoading={exportToPDF.isLoading}
      className={fullWidth ? 'w-full' : 'w-1/2'}
      trackId="foam-list-export-pdf"
    >
      .pdf
    </Button>
  );
};
